// Engine-side context + predicate / active-state evaluation for the sidebar.
// The engine builds a PanelCtx from the Clarix stack (react-router +
// @clarix/rbac) and resolves every declarative predicate against it — there are
// no functions in a config.

import type { Predicate, RowConfig } from '@clarix/shared-types';

export interface PanelCtx {
  /** RBAC checks. Loosely typed (string keys) so the engine stays generic. */
  can: (key: string) => boolean;
  canBase: (key: string) => boolean;
  canForProject: (key: string, projectId: string) => boolean;
  /** True when any project the user can access resolves the key. */
  canAnyProject: (key: string) => boolean;
  /** Whether a platform feature flag is on; absent, every `flag` predicate fails closed. */
  flagOn?: (code: string) => boolean;
  /** Whether the RBAC matrix has hydrated (drives `unloadedVisible`). */
  permissionsLoaded: boolean;
  params: URLSearchParams;
  pathname: string;
  /** Row id currently in scope (for `projectPermission` predicates). */
  scopeId?: string;
  /**
   * Ids of `toParam` rows that are the first VISIBLE row for their
   * (pathname, param) group — active when the param is absent. Only populated
   * when the panel opts in via `defaultActiveFirst`.
   */
  defaultActiveIds?: Set<string>;
}

function asArray<T>(v: T | T[] | undefined): T[] {
  if (v == null) return [];
  return Array.isArray(v) ? v : [v];
}

/** Evaluate a declarative visibility predicate. Missing predicate = visible. */
export function evalPredicate(pred: Predicate | undefined, ctx: PanelCtx): boolean {
  if (!pred) return true;
  if (pred.flag && ctx.flagOn?.(pred.flag) !== true) return false;

  if (pred.permission) {
    // Fail CLOSED while the matrix hydrates unless the config opts out — a row
    // that flashes and is then yanked away reads as a bug.
    if (!ctx.permissionsLoaded) return pred.unloadedVisible === true;
    const check = pred.anyProject ? ctx.canAnyProject : pred.base ? ctx.canBase : ctx.can;
    const keys = asArray(pred.permission);
    const ok = pred.permissionAny ? keys.some((k) => check(k)) : keys.every((k) => check(k));
    if (!ok) return false;
  }

  if (pred.projectPermission && ctx.scopeId) {
    if (!ctx.canForProject(pred.projectPermission, ctx.scopeId)) return false;
  }

  return true;
}

/** Path prefix match, mirroring SidebarRail's longest-prefix rule. */
function pathMatch(pathname: string, target: string, exact?: boolean): boolean {
  if (exact) return pathname === target;
  return pathname === target || pathname.startsWith(`${target}/`);
}

/** Extra param constraints (Spark feed vs admin; Review All vs a project). */
function activeExtraOk(row: RowConfig, ctx: PanelCtx): boolean {
  const ex = row.activeExtra;
  if (!ex) return true;
  if (ex.paramAbsent?.some((k) => ctx.params.get(k) != null)) return false;
  if (ex.paramNot && ctx.params.get(ex.paramNot.key) === ex.paramNot.value) return false;
  return true;
}

/** Whether a row should render as active for the current location. */
export function isRowActive(row: RowConfig, ctx: PanelCtx): boolean {
  if (row.neverActive) return false;
  // A provider that owns a bespoke selection rule (CRM's longest-match, its
  // master-config category default) has already decided — url matching must not
  // get a second opinion and light up a sibling row alongside it.
  if (row.forceActive !== undefined) return row.forceActive;
  if (!activeExtraOk(row, ctx)) return false;

  // Entity rows (projects) are active on any sub-route, regardless of `to`'s query.
  if (row.activePath) return pathMatch(ctx.pathname, row.activePath);

  if (row.toParam) {
    const p = row.toParam;
    const onPage =
      pathMatch(ctx.pathname, p.pathname) ||
      (row.matchPaths ?? []).some((m) => pathMatch(ctx.pathname, m));
    if (!onPage) return false;
    const cur = ctx.params.get(p.param);
    if (cur == null) return Boolean(p.isDefault) || (ctx.defaultActiveIds?.has(row.id) ?? false);
    return cur === p.value;
  }

  if (row.to) {
    const [path = '', query] = row.to.split('?');
    if (query) {
      if (!pathMatch(ctx.pathname, path)) return false;
      const qp = new URLSearchParams(query);
      for (const [k, v] of qp.entries()) {
        if (ctx.params.get(k) !== v) return false;
      }
      return true;
    }
    if (pathMatch(ctx.pathname, path, row.exact)) return true;
    return (row.matchPaths ?? []).some((m) => pathMatch(ctx.pathname, m, row.exact));
  }

  return false;
}

/**
 * The nav target for a row: `to`, or a param-based path — with any
 * `preserveParams` carried over from the current URL (e.g. the calendar view
 * survives opening the Schedule Meeting form).
 */
export function rowHref(row: RowConfig, ctx: PanelCtx): string | null {
  const base =
    row.to ??
    (row.toParam
      ? // Encoded: a param value is data (a master-list category code), and
        // `params.get()` decodes on the way back, so active-state still matches.
        `${row.toParam.pathname}?${row.toParam.param}=${encodeURIComponent(row.toParam.value)}`
      : null);
  if (base == null) return null;
  if (!row.preserveParams?.length) return base;

  const [path = '', query = ''] = base.split('?');
  const qp = new URLSearchParams(query);
  for (const key of row.preserveParams) {
    const v = ctx.params.get(key);
    // Explicit params on the row itself win over the preserved ones.
    if (v != null && !qp.has(key)) qp.set(key, v);
  }
  const q = qp.toString();
  return q ? `${path}?${q}` : path;
}
